import * as process from 'process';
import * as tus from 'tus-js-client';

import { SettingsManager } from '@classes/settingsManager';

export interface UploadedPath {
    path: string;
    compressed_path?: string;
}

export interface UseTusUploaderOptions {
    uploadDir: string;
    crmID: string;
    onFileUploaded?: (paths: UploadedPath, file: File) => void;
    onProgress?: (percentage: number, file: File) => void;
    onError?: (error: Error, file: File) => void;
}

const CHUNK_SIZE = 512 * 1024;
const PARALLEL_UPLOADS = 1; // 2 и больше роняет CORS

export const useTusUploader = ({
    uploadDir,
    crmID,
    onFileUploaded,
    onProgress,
    onError,
}: UseTusUploaderOptions) => {
    const creds = SettingsManager.getConnectionCredentials();

    const hasRequiredParams = (url: string): boolean => {
        const parsedUrl = new URL(url);
        const params = parsedUrl.searchParams;
        return params.has('uploadDir') && params.has('crm_id');
    };

    const uploadFile = async (file: File): Promise<UploadedPath> => {
        return new Promise((resolve, reject) => {
            const upload = new tus.Upload(file, {
                endpoint: `${process.env.API_URL}/api/v1/tus?crm_id=${crmID}&uploadDir=${uploadDir}`,
                headers: {
                    'Upload-Metadata': `filename ${btoa(unescape(encodeURIComponent(file.name)))}`,
                    Authorization: `Bearer ${creds?.token}`,
                },
                chunkSize: CHUNK_SIZE,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                parallelUploads: PARALLEL_UPLOADS,

                onUploadUrlAvailable: function () {
                    if (!hasRequiredParams(upload.url)) {
                        upload.url =
                            upload.url.replace('http://', 'https://') +
                            `?crm_id=${crmID}&uploadDir=${uploadDir}`;
                    }
                },

                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = +((bytesUploaded / bytesTotal) * 100).toFixed(2);
                    onProgress?.(percentage, file);
                },

                onSuccess: async () => {
                    try {
                        const resp = await fetch(upload.url, {
                            method: 'PATCH',
                            headers: { Authorization: `Bearer ${creds?.token}` },
                        });
                        const body = await resp.json();

                        const uploaded: UploadedPath = {
                            path: body?.original_path || body?.file_path,
                            compressed_path: body?.compressed_path || body?.file_path,
                        };

                        onFileUploaded?.(uploaded, file);
                        resolve(uploaded);
                    } catch (e) {
                        onError?.(e as Error, file);
                        reject(e);
                    }
                },

                onError: (error) => {
                    onError?.(error, file);
                    reject(error);
                },
            });

            upload.start();
        });
    };

    const uploadAll = async (files: File[]): Promise<UploadedPath[]> => {
        try {
            return await Promise.all(files.map(uploadFile));
        } catch (e) {
            console.error('Ошибка загрузки');
            console.error(e);
            return null;
        }
    };

    return {
        uploadFile,
        uploadAll,
    };
};
